import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useToast } from "@/hooks/use-toast";
import { orpc, rpcClient } from "@/lib/rpc";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onboardingId: string;
  currentBuddyId: string | null;
}

export function ChangeBuddyDialog({ open, onOpenChange, onboardingId, currentBuddyId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const buddyCandidatesQuery = useQuery({
    ...orpc.onboardings.listBuddyCandidates.queryOptions(),
    enabled: open,
  });
  const candidates = buddyCandidatesQuery.data ?? [];

  const [buddyEmail, setBuddyEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setBuddyEmail("");
      setError(null);
    }
  }, [open]);

  const options = useMemo(
    () =>
      candidates
        .filter((e) => e.id !== currentBuddyId)
        .map((e) => ({
          value: e.corporateEmail,
          label: `${e.fullName} · ${e.corporateEmail}${
            e.isAvailable === false ? " (em onboarding ativo)" : ""
          }`,
          disabled: e.isAvailable === false,
        })),
    [candidates, currentBuddyId],
  );

  const change = useMutation({
    mutationFn: (input: { id: string; buddyEmail: string }) =>
      rpcClient.onboardings.changeBuddy(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.getById.queryOptions({ input: { id: onboardingId } }).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listActive.queryOptions().queryKey,
      });
      toast({ title: "Buddy alterado" });
      onOpenChange(false);
    },
    onError: (err: any) => {
      const code = err?.data?.code as string | undefined;
      const message = err?.message ?? "Não foi possível trocar o buddy.";
      if (
        code === "buddy_not_found" ||
        code === "buddy_not_eligible" ||
        code === "buddy_at_capacity" ||
        code === "buddy_unchanged"
      ) {
        setError(message);
        return;
      }
      toast({
        title: "Erro ao trocar buddy",
        description: message,
        variant: "destructive",
      });
    },
  });

  function handleSubmit() {
    setError(null);
    if (!buddyEmail) {
      setError("Selecione um buddy");
      return;
    }
    change.mutate({ id: onboardingId, buddyEmail });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Trocar buddy</DialogTitle>
          <DialogDescription>
            Escolha um novo buddy elegível. O buddy atual deixará de acompanhar este onboarding.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label>Novo buddy</Label>
          <SearchableSelect
            value={buddyEmail || undefined}
            onValueChange={(v) => {
              setBuddyEmail(v);
              setError(null);
            }}
            options={options}
            placeholder={
              buddyCandidatesQuery.isLoading
                ? "Carregando..."
                : options.length === 0
                  ? "Nenhum buddy disponível"
                  : "Selecione um colaborador"
            }
            disabled={buddyCandidatesQuery.isLoading || options.length === 0}
            data-testid="select-change-buddy"
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={change.isPending}>
            Voltar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={change.isPending || !buddyEmail}
            data-testid="button-confirm-change-buddy"
          >
            {change.isPending ? "Salvando..." : "Trocar buddy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
